import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { BillingLedgerEntry } from "../api/billingApi";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";

interface BillingHistoryListProps {
  entries: BillingLedgerEntry[];
  isLoading: boolean;
  error: string | null;
}

const badgeClasses =
  "inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300";
const statusTextClasses = "text-sm text-slate-500 dark:text-slate-400";
const statusErrorClasses = "text-sm text-rose-600 dark:text-rose-400";
const amountClasses =
  "flex items-center gap-2 text-lg font-semibold tracking-tight";

export function BillingHistoryList({ entries, isLoading, error }: BillingHistoryListProps) {
  const { t } = useTranslation();
  const rows = useMemo(() => entries, [entries]);

  if (isLoading) {
    return <p className={statusTextClasses}>{t("common.loading")}</p>;
  }

  if (error) {
    return <p className={statusErrorClasses}>{t("billing.history.error")}</p>;
  }

  if (rows.length === 0) {
    return <p className={statusTextClasses}>{t("billing.history.empty")}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {rows.map((entry) => {
        const amountTone =
          entry.amount_credits > 0
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-rose-600 dark:text-rose-400";
        const entryTitle = t(`billing.entries.${entry.entry_type}`, {
          defaultValue: entry.entry_type,
        });
        const entryDate = new Date(entry.created_at).toLocaleString([], {
          dateStyle: "medium",
          timeStyle: "short",
        });

        return (
          <SurfaceCard key={entry.id} tone="default" className="flex flex-col gap-3 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {entryTitle}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{entryDate}</p>
              </div>
              <div className={amountClasses}>
                <span className={amountTone}>
                  {entry.amount_credits > 0 ? "+" : ""}
                  {entry.amount_credits}
                </span>
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("billing.card.credits_label")}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              {entry.workspace_id ? (
                <span className={badgeClasses}>{entry.workspace_id}</span>
              ) : (
                <span className={badgeClasses}>{t("billing.history.personal_scope")}</span>
              )}
              {entry.request_id ? (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                  {entry.request_id}
                </span>
              ) : null}
            </div>
          </SurfaceCard>
        );
      })}
    </div>
  );
}
